'use client';

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { safeLocalStorage } from '@/lib/browser/safe-local-storage';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
import { logger } from '@/lib/telemetry/logger';

export const MAX_RECENT_ENTRIES: number = 6;
export const RECENT_SEARCHES_STORAGE_KEY: string = 'morfi-recent-searches';
export const RECENT_SEARCHES_VERSION: number = 1;

export interface RecentSearchEntry {
  query: string;
  ts: number;
}

interface RecentSearchesState {
  entries: RecentSearchEntry[];
  addRecent: (query: string) => void;
  clearRecent: () => void;
}

export const useRecentSearchesStore: UseBoundStore<StoreApi<RecentSearchesState>> =
  createTrackedStore<RecentSearchesState>('useRecentSearchesStore')(
    persist(
      (set, get) => ({
        entries: [],

        addRecent: (query: string): void => {
          const trimmed = query.trim();
          if (trimmed.length === 0) return;

          const lower = trimmed.toLocaleLowerCase('en-US');
          const filtered = get().entries.filter(
            (entry) => entry.query.toLocaleLowerCase('en-US') !== lower,
          );
          const next: RecentSearchEntry[] = [{ query: trimmed, ts: Date.now() }, ...filtered].slice(
            0,
            MAX_RECENT_ENTRIES,
          );

          set({ entries: next });
        },

        clearRecent: (): void => {
          set({ entries: [] });
        },
      }),
      {
        name: RECENT_SEARCHES_STORAGE_KEY,
        version: RECENT_SEARCHES_VERSION,
        storage: createJSONStorage(() => safeLocalStorage),
        onRehydrateStorage:
          () =>
          // ALLOW-UNKNOWN: zustand persist's onRehydrateStorage callback signature uses `unknown` for error.
          (_state: RecentSearchesState | undefined, error: unknown): void => {
            if (error !== undefined && error !== null) {
              logger.warn('recent-searches.persist_unavailable', {
                op: 'rehydrate',
                message: error instanceof Error ? error.message.slice(0, 200) : 'unknown',
              });
            }
          },
      },
    ),
  );
